import { theme } from '@policygenius/mortar';
import styled, { css } from 'styled-components';
import { fontWeight, suisseIntl, victorSerif } from 'pages/ReferralLandingPage/typography';

const DESKTOP_BREAKPOINT = 1176;

export const Section = styled.section`
  overflow: hidden;
  width: 100%;
  padding: 48px 16px;

  @media (min-width: ${theme.breakpoints.medium}px) {
    padding: 64px 16px;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    padding: 40px 48px;
  }
`;

export const WrapperSection = styled.div<{ $inheritBackground?: boolean }>`
  background-color: #f7f5f3;
  border-radius: 12px;
  padding: 8px 0;

  ${({ $inheritBackground }) =>
    $inheritBackground &&
    css`
      background-color: inherit;
    `}

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    padding: 80px 16px;

    ${({ $inheritBackground }) =>
      $inheritBackground &&
      css`
        padding: 0;
      `}
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  margin: 0 auto;
  width: 100%;
  max-width: 1280px;
  padding: 0;

  @media (min-width: ${theme.breakpoints.medium}px) {
    gap: 48px;
    padding: 0 48px;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    gap: 40px;
    padding: 0;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  width: 100%;
`;

export const Title = styled.h2`
  ${victorSerif()};
  margin: 0;
  color: #131212;
  font-size: 34px;
  line-height: 1.1;
  letter-spacing: 0;
  position: relative;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 64px;
  }
`;

export const TitleItalic = styled.em`
  font-style: italic;
`;

export const TitleWordWrapper = styled.span`
  display: inline-block;
  position: relative;
`;

export const TitleUnderlineWrap = styled.span`
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 69px;
  height: 11px;
  line-height: 0;
  pointer-events: none;

  @media (min-width: ${theme.breakpoints.medium}px) {
    bottom: -8px;
    width: 130px;
    height: 16px;
  }
`;

export const Subtitle = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 18px;
  line-height: 1.3;
  max-width: 100%;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 24px;
    line-height: 1.3;
    max-width: 820px;
  }
`;

export const Carousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 1229px;
  margin-inline: auto;
`;

export const CarouselLiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const CarouselViewport = styled.div`
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
`;

export const CarouselTrack = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CarouselSlide = styled.div`
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
`;

export const TestimonialCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-height: 367px;
  padding: 24px;
  border-radius: 12px;
  background-color: #f7f5f3;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  @media (min-width: ${theme.breakpoints.medium}px) {
    min-height: 370px;
  }
`;

export const CarouselCardLink = styled.a`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 280px;
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.95;
    transform: scale(1.01);
  }

  &:hover ${TestimonialCard} {
    box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  }
`;

export const CarouselNav = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px;
  align-items: center;
  column-gap: 16px;
  margin-top: 24px;
  margin-inline: auto;
  width: 100%;
  max-width: fit-content;
`;

export const CarouselButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #eae5e1;
  border-radius: 12px;
  background-color: #ffffff;
  color: #131212;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: #f7f5f3;
    border-color: #d9d9d9;
  }

  &:focus-visible {
    outline: 2px solid #eb6424;
    outline-offset: 2px;
  }

  &:first-of-type {
    justify-self: start;
  }

  &:last-of-type {
    justify-self: end;
  }
`;

export const CarouselDots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  justify-self: center;
  min-width: 0;
  max-width: 100%;
`;

export const CarouselDot = styled.button<{ $active?: boolean }>`
  width: 16px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 2px;
  background-color: #131212;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${({ $active }) => ($active ? 0.85 : 0.25)};
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: ${({ $active }) => ($active ? 0.85 : 0.45)};
  }

  &:focus-visible {
    outline: 2px solid #eb6424;
    outline-offset: 2px;
  }
`;

export const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;
`;

export const StarsRow = styled.div`
  line-height: 0;
  flex-shrink: 0;
`;

export const TrustpilotStarsImg = styled.img`
  display: block;
  width: 98px;
  height: 18px;
`;

export const VerifiedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: #787471;
`;

export const VerifiedIconWrap = styled.span`
  display: flex;
  flex-shrink: 0;
  line-height: 0;
  color: #787471;

  svg {
    display: block;
    width: 20px;
    height: 20px;
  }
`;

export const VerifiedLabel = styled.span`
  ${suisseIntl(fontWeight.medium)};
  font-size: 14px;
  line-height: 1.3;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 14px;
    line-height: 1.3;
  }
`;

export const CardQuoteTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  flex-shrink: 0;
`;

export const QuoteIconWrap = styled.div`
  display: flex;
  flex-shrink: 0;
  padding-top: 2px;
  line-height: 0;
  color: #eb6424;

  svg {
    display: block;
    width: 32px;
    height: 32px;
  }

  @media (min-width: ${theme.breakpoints.medium}px) {
    svg {
      width: 36px;
      height: 36px;
    }
  }
`;

export const ReviewTitle = styled.h3`
  ${suisseIntl(fontWeight.medium)};
  flex: 1;
  min-width: 0;
  margin: 12px 0 0;
  color: #131212;
  font-size: 18px;
  line-height: 1.1;
`;

export const QuoteWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Quote = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 16px;
  line-height: 1.3;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 16px;
    line-height: 1.3;
  }
`;

export const CardFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
`;

export const AuthorMeta = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 14px;
  line-height: 1.3;
`;

export const AuthorNameStrong = styled.span`
  font-weight: 700;
`;

export const AuthorSep = styled.span`
  font-weight: 400;
  opacity: 0.75;
`;

export const AuthorDate = styled.span`
  color: #787471;
  font-weight: 400;
`;

export const TrustpilotFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  @media (min-width: ${theme.breakpoints.medium}px) {
    flex-flow: row wrap;
    justify-content: center;
  }
`;

export const RatingLine = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 14px;
  line-height: 3;
`;

export const TrustpilotLine = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0;
  color: #131212;
  font-size: 14px;
  line-height: 3;
`;

export const TrustpilotLink = styled.a`
  ${suisseIntl(fontWeight.bold)};
  color: #131212;
  margin-left: 4px;
  text-decoration: underline;
  text-underline-offset: 0.12em;

  &:hover {
    color: #eb6424;
  }
`;

export const TrustpilotLogo = styled.img`
  display: inline-block;
  width: 145px;
  max-height: 34px;
  height: auto;
  margin-left: 4px;
  vertical-align: text-bottom;
`;
